import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { LinkList } from '@/design-system/molecules/LinkList';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { trackInteraction } from '@/utils/analytics';
import { scrollIntoView } from '@/utils/scrollIntoView';

import styles from './AcceptedInsurances.module.scss';

const MOBILE_ITEMS_TO_DISPLAY = 5;
const DESKTOP_ITEMS_TO_DISPLAY = 10;

/**
 * Accepted Insurances displays a list of accepted insurances with a Show More/Show Less button
 */
const AcceptedInsurances = ({
    htmlId,
    title,
    description,
    showMoreText,
    showLessText,
    insurancesList,
    ...props
}) => {
    const breakpoint = useBreakpoint();
    const isMobile = !breakpoint?.large;
    const [listItems, setListItems] = useState([]);
    const [itemsToDisplay, setItemsToDisplay] = useState(0);
    const [ctaLabel, setCtaLabel] = useState(showMoreText);
    const showCta =
        insurancesList.length > (isMobile ? MOBILE_ITEMS_TO_DISPLAY : DESKTOP_ITEMS_TO_DISPLAY);

    // Set the initial number of list items to display based on the breakpoint
    useEffect(() => {
        if (isMobile) {
            setItemsToDisplay(MOBILE_ITEMS_TO_DISPLAY);
        } else {
            setItemsToDisplay(DESKTOP_ITEMS_TO_DISPLAY);
        }
    }, [isMobile]);

    // Update listItems and CTA label when itemsToDisplay changes
    useEffect(() => {
        setListItems(insurancesList.slice(0, itemsToDisplay).map((item) => ({ label: item })));
        setCtaLabel(itemsToDisplay < insurancesList.length ? showMoreText : showLessText);
    }, [insurancesList, itemsToDisplay, showLessText, showMoreText]);

    /**
     * Handle clicking the Show More/Show Less button
     * @param {Event} event
     * @returns {void}
     */
    const handleShowButton = (event) => {
        event.preventDefault();
        if (itemsToDisplay < insurancesList.length) {
            setItemsToDisplay(insurancesList.length);
        } else {
            setItemsToDisplay(isMobile ? MOBILE_ITEMS_TO_DISPLAY : DESKTOP_ITEMS_TO_DISPLAY);
            scrollIntoView(htmlId);
        }

        // Analytics
        trackInteraction({
            actionLabel: ctaLabel,
            componentName: 'fad_bio_insurance',
            componentTitle: title,
            eventName: 'click',
            interactionType: 'fad_bio_cta',
            selector: 'button',
        });
    };

    return (
        <div className={styles[`accepted-insurances`]} id={htmlId}>
            <LinkList
                {...props}
                title={title}
                subTitle={description}
                listItems={listItems}
                cta={
                    showCta
                        ? {
                              label: ctaLabel,
                              onClick: handleShowButton,
                          }
                        : null
                }
            />
        </div>
    );
};

AcceptedInsurances.propTypes = {
    htmlId: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    showMoreText: PropTypes.string,
    showLessText: PropTypes.string,
    insurancesList: PropTypes.arrayOf(PropTypes.string),
};

AcceptedInsurances.defaultProps = {
    htmlId: 'insurance',
    title: 'Insurance',
    description: 'Please contact the practice to confirm your health plan is accepted.',
    showMoreText: 'Show More',
    showLessText: 'Show Less',
    insurancesList: [],
};

export default AcceptedInsurances;
